import { inject, Injectable } from '@angular/core';
import { BulkStudentUsageBindingModel } from '../models/bulk-student-usage-binding-model';
import { InternalStudentUsageReturnModel } from '../models/internal-student-usage-return-model';
import { StudentReadingLevelReturnModel } from '../models/student-reading-level-return-model';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { StudentMathsActivity } from '../models/student-math-activity-response-model.interface';
import { HttpAction } from '../studio-http-client/http-action.enum';
import { StudentResponseWithClassIds } from '../models/student-response-with-classIds';
import { HttpContext, HttpHeaders } from '@angular/common/http';
import { StudentResponse } from '../models/student-response';
import { RequiredBy } from '../../shared/utils/utility-types/required-by';
import { BookTagsRequestModel } from '../models/book-tags-request.model';
import { HttpClientUtils } from '../studio-http-client/http-client-utils';
import { FastSubShelf } from '../models/fast-sub-shelf';

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  apiService = inject(ApiService);

  getStudent(studentId: string, context: HttpContext = null): Observable<StudentResponse> {
    const getURL = `api/Students/${studentId}`;
    return this.apiService.invokeLSApi(getURL, HttpAction.Get, null, { context });
  }

  getStudentReadingLevels(payload: Omit<BulkStudentUsageBindingModel, 'classId'>) {
    const url = 'v1/internal/students/GetStudentReadingLevels';

    return this.apiService.invokeMPApi<StudentReadingLevelReturnModel[]>(
      url,
      HttpAction.PostWithBodyResponse,
      payload
    );
  }

  getStudentCoachUsage(payload: BulkStudentUsageBindingModel) {
    const url = 'v1/internal/usage/GetStudentUsage';

    return this.apiService.invokeMPApi<InternalStudentUsageReturnModel[]>(
      url,
      HttpAction.PostWithBodyResponse,
      payload
    );
  }

  getStudentMathsActivity(
    payload: BulkStudentUsageBindingModel
  ): Observable<StudentMathsActivity[]> {
    const url = 'v1/internal/usage/GetStudentMathUsageAndSkillLevel';

    return this.apiService.invokeMPApi(url, HttpAction.PostWithBodyResponse, payload);
  }

  getStudentsBySchoolAndClasses(
    schoolId: string,
    classIds: string[]
  ): Observable<StudentResponseWithClassIds[]> {
    const url = `api/Schools/${schoolId}/ClassStudents`;
    return this.apiService.invokeLSApi<StudentResponseWithClassIds[]>(
      url,
      HttpAction.PostWithBodyResponse,
      classIds,
      { additionalHeaders: new HttpHeaders({ SchoolId: schoolId }) }
    );
  }

  getBookTagsForUserIdAndShelfId({
    userId,
    userGroupId,
    bookTagType,
    userGroupOrganizationId,
    shelfId,
    grade,
    searchTerm,
    formatType,
    searchOnShelfBookTagsOnly,
    onlyLightSailReady = true,
    excludeDrm = false,
    excludeNonDrm = false,
    take = 1000,
    religionTags,
    noReligion,
    privateLibrary = null,
    minAvailableCommunityContentPrivacy = null,
    maxAvailableCommunityContentPrivacy = null,
    availableCommunityContent = null,
    communityLibraries = null,
    bookTagId = null,
    decodableMapIds = null,
    decodableDistinctions = null,
    classification = null,
  }: RequiredBy<
    BookTagsRequestModel,
    'userGroupOrganizationId' | 'userGroupId' | 'userId' | 'shelfId'
  >) {
    const queryParams = HttpClientUtils.createURLSearchParams({
      bookTagType,
      onlyLightSailReady,
      excludeDrm,
      excludeNonDrm,
      grade,
      searchTerm,
      formatType,
      searchOnShelfBookTagsOnly,
      take,
      religionTags,
      noReligion,
      privateLibrary,
      minAvailableCommunityContentPrivacy,
      maxAvailableCommunityContentPrivacy,
      availableCommunityContent,
      communityLibraries,
      bookTagId,
      decodableMapIds,
      decodableDistinctions,
      classification,
    });
    const getURL = `api/UserGroupOrganizations/${userGroupOrganizationId}/UserGroups/${userGroupId}/Users/${userId}/Shelves/${shelfId}/FastSubShelves${queryParams}`;
    return this.apiService.invokeLSApi<FastSubShelf[]>(getURL, HttpAction.Get);
  }
}
